import { useEffect, useRef, useState } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import { Accordion, Button as MantineButton } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { AlertTriangle, Check } from 'lucide-react'
import { v1 as uuid } from 'uuid'

import {
  EndorsementDescriptions as LicenseEndorsementDescriptions,
  LicenseClass,
} from 'src/graphql/types/licenses'
import IconSparkle from 'src/icons/IconSparkle'
import { CountryConfig } from 'src/lib/country.utils'
import { valuesMismatch } from 'src/lib/documentIntelligence'
import {
  getEndorsementsByLicenseClass,
  getLicenseClassOptions,
  isMedicalExamExpirationRequired,
  resolveScannedLicensePrefill,
} from 'src/lib/license.utils'
import { findLicenseRegion } from 'src/lib/usStates'

import ApplicationAccordionItem from '../components/ApplicationAccordionItem'
import ApplicationAddressInput from '../components/ApplicationAddressInput'
import ApplicationMultiSelectInput from '../components/ApplicationMultiSelectInput'
import ApplicationRadioQuestion from '../components/ApplicationRadioQuestion'
import ApplicationScreenWrapper from '../components/ApplicationScreenWrapper'
import ApplicationSelectInput from '../components/ApplicationSelectInput'
import ApplicationTextInput from '../components/ApplicationTextInput'
import CdlRetakeContent from '../components/CdlRetakeContent'
import CdlScanBanner from '../components/CdlScanBanner'
import CdlScanDesktopRail from '../components/CdlScanDesktopRail'
import {
  HIGH_CONFIDENCE_THRESHOLD,
  useDriverCdlAutofill,
  type CdlPrefill,
  type CdlPrefillConfidence,
  type CdlScanSource,
} from '../hooks/useDriverCdlAutofill'
import { useFormLeaveGuard } from '../hooks/useLeaveGuard'
import { formatDate, formatDateString } from '../utils/formatters'
import { useFormContext } from '../utils/FormContext'
import { buildLicensePayload } from '../utils/stepPayloads'
import { findErrors } from '../utils/validators'

export const LicenseScreen = () => {
  const { form, handleNext, updateLoading, application, backText } =
    useFormContext()
  const { goBack, leaveGuardModal } = useFormLeaveGuard(buildLicensePayload)

  const [hasTWICCard, setHasTWICCard] = useState(
    application?.twicCard?.cardNumber || application?.twicCard?.expirationDate
  )
  const [hasPreviousLicenses, setHasPreviousLicenses] = useState(
    application?.previousLicenses && application?.previousLicenses.length > 0
  )
  const [endorsementDisabled, setEndorsementDisabled] = useState(false)
  const [medicalExamDisabled, setMedicalExamDisabled] = useState(false)
  const [accordionValue, setAccordionValue] = useState(null)

  // ── CDL autofill ──────────────────────────────────────────────────────────
  // The Tailwind `sm` breakpoint (640px) splits the two scan UIs: below it the
  // mobile banner + source-sheet flow renders; at/above it the desktop split
  // rail (ENG-4358) does. Both default to `false` on the server / first paint
  // so neither UI flashes before its media query resolves.
  const isMobile = useMediaQuery('(max-width: 639px)', false)
  const isDesktop = useMediaQuery('(min-width: 640px)', false)
  const applicantId = application?.applicant?.applicantId
  // Returning applicant (RUM dimension, ENG-4362): the application already
  // carried saved license data when the step loaded — the same signal the
  // "Double-check this" conflict chips key on (originalLicense).
  const returningApplicant = Boolean(
    application?.license?.licenseNumber ||
      application?.license?.state ||
      application?.license?.expirationDate
  )
  const {
    status: cdlStatus,
    prefill: cdlPrefill,
    confidence: cdlConfidence,
    start: startCdlAutofill,
    reset: resetCdlAutofill,
  } = useDriverCdlAutofill(applicantId, returningApplicant)

  // The file the desktop rail is scanning: its name captions the review image,
  // and a preview URL shows it in the uploading/review panes. Images get a
  // plain object URL — even when the OS supplies an EMPTY `file.type` for a
  // perfectly renderable photo, the rail's <img> falls back to a placeholder
  // itself if the browser can't decode it. PDFs get their FIRST PAGE rendered
  // to a data URL via pdf.js (react-pdf's worker is registered app-wide in
  // App.tsx; the import is dynamic so the library loads only when a PDF is
  // actually picked).
  const [cdlScanFile, setCdlScanFile] = useState<File | null>(null)
  const [cdlPreviewUrl, setCdlPreviewUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!cdlScanFile) {
      setCdlPreviewUrl(null)
      return
    }
    const isPdf =
      cdlScanFile.type === 'application/pdf' || /\.pdf$/i.test(cdlScanFile.name)
    if (!isPdf) {
      const url = URL.createObjectURL(cdlScanFile)
      setCdlPreviewUrl(url)
      return () => URL.revokeObjectURL(url)
    }
    // PDF: rasterize page 1. `cancelled` guards against a slow render landing
    // after the driver already picked a different file (or reset).
    let cancelled = false
    setCdlPreviewUrl(null)
    ;(async () => {
      try {
        const { pdfjs } = await import('react-pdf')
        const data = await cdlScanFile.arrayBuffer()
        const pdf = await pdfjs.getDocument({ data }).promise
        try {
          const page = await pdf.getPage(1)
          // Render at ~2x the pane width so the preview stays sharp on
          // high-DPI screens without rasterizing the whole page at print size.
          const base = page.getViewport({ scale: 1 })
          const viewport = page.getViewport({ scale: 800 / base.width })
          const canvas = document.createElement('canvas')
          canvas.width = viewport.width
          canvas.height = viewport.height
          const canvasContext = canvas.getContext('2d')
          if (!canvasContext) return
          await page.render({ canvasContext, viewport }).promise
          if (!cancelled) setCdlPreviewUrl(canvas.toDataURL('image/png'))
        } finally {
          pdf.destroy()
        }
      } catch {
        // Corrupt/encrypted PDF — the rail keeps its placeholder pattern.
        if (!cancelled) setCdlPreviewUrl(null)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [cdlScanFile])

  // EVERY scan entry point (mobile banner, retake screen, desktop rail)
  // records the file before starting the scan: the hook's status is shared
  // across the breakpoint, so the preview state must be too — a scan started
  // on mobile that crosses 640px mid-scan (tablet rotation) would otherwise
  // reach the desktop rail with no document to show.
  const handleCdlFileSelected = (
    file: File,
    source: CdlScanSource,
    interaction?: 'drag' | 'browse'
  ) => {
    // Clear the previous preview SYNCHRONOUSLY: this render already flips the
    // hook to 'uploading', but the preview effect only swaps the URL after
    // commit — without this, a retry briefly shows the PREVIOUS photo under
    // "Uploading…" in the rail.
    setCdlPreviewUrl(null)
    setCdlScanFile(file)
    startCdlAutofill(file, source, interaction)
  }

  // Desktop rail entry point: drag-drop and click-to-browse both report the
  // 'desktop_upload' RUM route; interaction distinguishes them (ENG-4362).
  const handleDesktopFileSelected = (
    file: File,
    interaction: 'drag' | 'browse'
  ) => {
    handleCdlFileSelected(file, 'desktop_upload', interaction)
  }

  // The confident AI values (and their per-field confidence), kept after prefill
  // so each field can render its nudges ("Please add" / "Double-check this").
  const [aiValues, setAiValues] = useState<CdlPrefill | null>(null)
  const [aiConfidence, setAiConfidence] = useState<CdlPrefillConfidence | null>(
    null
  )
  // Fields the most recent scan actually wrote into the form, so a rescan that
  // returns fewer fields can clear the ones the previous scan filled but this
  // one didn't. Emptied by handleCdlReset, which reverts untouched scan-written
  // values itself — after a reset, whatever remains in the form is driver-owned
  // and must not be blanked by a later partial scan.
  const lastAppliedFields = useRef<Set<keyof CdlPrefill>>(new Set())

  // True while the current scan attempt follows a failed one (unreadable or
  // not-a-CDL) — the analyzing takeover then reads "Re-reading your CDL
  // front…" (mockup Path 3) instead of "Analyzing document…". Cleared when a
  // scan fills or the driver leaves the scan flow (reset → idle).
  const [hadRejectedScan, setHadRejectedScan] = useState(false)
  useEffect(() => {
    if (cdlStatus === 'rejected' || cdlStatus === 'unreadable')
      setHadRejectedScan(true)
    else if (cdlStatus === 'filled' || cdlStatus === 'idle')
      setHadRejectedScan(false)
  }, [cdlStatus])

  const classTypes = [
    { value: null, label: 'Select a class type' },
    ...getLicenseClassOptions(form.values.license?.country),
  ]

  const previousClassTypes = (country?: string | null) => [
    { value: null, label: 'Select a class type' },
    ...getLicenseClassOptions(country),
  ]

  const handleClassTypeChange = (
    value,
    country = form.values.license?.country
  ) => {
    form.setFieldValue('license.licenseClass', value)
    if (value === LicenseClass.NO_CDL) {
      form.setFieldValue('license.endorsements', [])
      setEndorsementDisabled(true)
    } else {
      if (endorsementDisabled) {
        setEndorsementDisabled(false)
      }
    }

    if (isMedicalExamExpirationRequired(value, country)) {
      if (medicalExamDisabled) {
        form.setFieldValue('license.medicalExamExpirationDate', undefined)
      }
      setMedicalExamDisabled(false)
    } else {
      form.setFieldValue('license.medicalExamExpirationDate', null)
      setMedicalExamDisabled(true)
    }
  }

  // Apply the AI prefill once, when autofill resolves to 'filled'. Fills
  // license number / state / class / expiration / endorsements — the
  // medical-exam date stays manual per the spec. A rescan that returns fewer
  // fields clears the fields the previous scan filled but this one didn't, so a
  // stale value from the rescanned-away photo isn't silently left behind
  // untagged.
  useEffect(() => {
    if (cdlStatus !== 'filled' || !cdlPrefill) return

    // Country must be known before class coercion: a scanned Alberta CDL
    // should land on CA + CLASS_1, not get stamped US and drop the class.
    // Unrecognized OCR states leave country alone (do not force US).
    const applied: CdlPrefill = { ...cdlPrefill }
    const resolved = resolveScannedLicensePrefill(
      applied,
      form.values.license?.country
    )
    if (resolved.state !== undefined) applied.state = resolved.state
    if (resolved.licenseClass !== undefined) {
      applied.licenseClass = resolved.licenseClass
    } else {
      delete applied.licenseClass
    }
    const countryForPrefill = resolved.country
    const scannedRegion = findLicenseRegion(applied.state)

    const prev = lastAppliedFields.current
    const nextApplied = new Set<keyof CdlPrefill>()

    if (applied.state !== undefined) {
      form.setFieldValue('license.state', applied.state)
      if (scannedRegion) {
        form.setFieldValue('license.country', scannedRegion.country)
      }
      nextApplied.add('state')
    }
    if (applied.licenseClass !== undefined) {
      handleClassTypeChange(applied.licenseClass, countryForPrefill)
      nextApplied.add('licenseClass')
    } else if (prev.has('licenseClass')) {
      handleClassTypeChange(null, countryForPrefill)
    }
    if (applied.licenseNumber !== undefined) {
      form.setFieldValue('license.licenseNumber', applied.licenseNumber)
      nextApplied.add('licenseNumber')
    } else if (prev.has('licenseNumber')) {
      form.setFieldValue('license.licenseNumber', '')
    }
    if (applied.expirationDate !== undefined) {
      form.setFieldValue('license.expirationDate', applied.expirationDate)
      nextApplied.add('expirationDate')
    } else if (prev.has('expirationDate')) {
      form.setFieldValue('license.expirationDate', '')
    }

    // Endorsements apply only where the multiselect actually renders (US
    // license, class ≠ NO_CDL — handleClassTypeChange above just cleared and
    // disabled it for NO_CDL) and only codes valid for the class that ended up
    // applied: the dropdown offers class-valid options only, so an off-class
    // code would be invisible-but-submitted form state.
    const effectiveCountry = countryForPrefill
    const effectiveClass =
      applied.licenseClass ?? form.values.license?.licenseClass
    const appliedEndorsements =
      applied.endorsements &&
      effectiveCountry === 'US' &&
      effectiveClass !== LicenseClass.NO_CDL
        ? applied.endorsements.filter((code) =>
            (
              getEndorsementsByLicenseClass(effectiveClass) as string[]
            ).includes(code)
          )
        : undefined

    if (appliedEndorsements !== undefined) {
      applied.endorsements = appliedEndorsements
      form.setFieldValue('license.endorsements', appliedEndorsements)
      nextApplied.add('endorsements')
    } else {
      // Not applied (all off-class, or field hidden) — drop it from the
      // provenance values so the nudges don't reference a value the form never
      // received, and clear a previous scan's endorsements like the other fields.
      delete applied.endorsements
      if (prev.has('endorsements')) {
        form.setFieldValue('license.endorsements', [])
      }
    }

    lastAppliedFields.current = nextApplied
    setAiValues(applied)
    setAiConfidence(cdlConfidence)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cdlStatus, cdlPrefill])

  // Clearing autofill (Rescan / Try again / Skip) drops the AI provenance tags
  // AND reverts any field the discarded scan wrote that the driver hasn't
  // edited since — otherwise the form keeps values from a photo the driver
  // just threw away, with no tag saying where they came from. Fields the
  // driver touched after the prefill are theirs and stay.
  const handleCdlReset = () => {
    if (aiValues) {
      if (
        aiValues.state !== undefined &&
        form.values.license?.state === aiValues.state
      ) {
        const scannedCountry = findLicenseRegion(aiValues.state)?.country
        form.setFieldValue('license.state', '')
        if (scannedCountry && form.values.license?.country === scannedCountry) {
          form.setFieldValue('license.country', CountryConfig.US.code)
        }
      }
      if (
        aiValues.licenseClass !== undefined &&
        form.values.license?.licenseClass === aiValues.licenseClass
      ) {
        handleClassTypeChange(null)
      }
      if (
        aiValues.licenseNumber !== undefined &&
        form.values.license?.licenseNumber === aiValues.licenseNumber
      ) {
        form.setFieldValue('license.licenseNumber', '')
      }
      if (
        aiValues.expirationDate !== undefined &&
        formatDateString(form.values.license?.expirationDate) ===
          formatDateString(aiValues.expirationDate)
      ) {
        form.setFieldValue('license.expirationDate', '')
      }
      if (
        aiValues.endorsements !== undefined &&
        (form.values.license?.endorsements ?? []).join(',') ===
          aiValues.endorsements.join(',')
      ) {
        form.setFieldValue('license.endorsements', [])
      }
    }
    // The scan's writes are gone (or driver-owned now), so the next scan must
    // not treat them as its own leftovers — a stale set here would let a
    // partial rescan blank fields the driver typed between attempts.
    lastAppliedFields.current = new Set()
    setAiValues(null)
    setAiConfidence(null)
    // Desktop rail bookkeeping: the discarded scan's file/preview go with it,
    // so the rail returns to a clean dropzone (no-op on mobile, always null).
    setCdlScanFile(null)
    resetCdlAutofill()
  }

  // Two per-field nudges after a scan:
  //  • "Please add" — a required field the AI left blank (prediction <
  //    the backend's display threshold) that has no value yet; the driver types it. Clears once set.
  //  • "Double-check this" — either of:
  //      (a) conflict: the AI value DIFFERS from the applicant's EXISTING
  //          (non-empty) application value — returning applicants only; or
  //      (b) borderline read: the field prefilled at < HIGH_CONFIDENCE (0.9) and
  //          still holds the AI value — a plausible-but-unverified OCR read.
  //          Clears once the driver edits the value (they've taken ownership).
  //    Both compares are field-aware (formatting / leading zeros don't count).
  const scanCompleted = !!aiValues
  const originalLicense = application?.license

  const stateNeedsAdd = scanCompleted && !form.values.license?.state
  const classNeedsAdd = scanCompleted && !form.values.license?.licenseClass
  const licenseNumberNeedsAdd =
    scanCompleted && !form.values.license?.licenseNumber
  const expirationNeedsAdd =
    scanCompleted && !form.values.license?.expirationDate
  const medicalExamNeedsAdd =
    scanCompleted &&
    !medicalExamDisabled &&
    !form.values.license?.medicalExamExpirationDate

  // (a) conflict with the applicant's existing application value
  const stateConflict =
    aiValues?.state !== undefined &&
    originalLicense?.state !== undefined &&
    valuesMismatch(
      'LICENSE_STATE',
      originalLicense.state,
      form.values.license?.state
    )
  const classConflict =
    aiValues?.licenseClass !== undefined &&
    originalLicense?.licenseClass !== undefined &&
    valuesMismatch(
      'LICENSE_CLASS',
      originalLicense.licenseClass,
      form.values.license?.licenseClass
    )
  const licenseNumberConflict =
    aiValues?.licenseNumber !== undefined &&
    originalLicense?.licenseNumber !== undefined &&
    valuesMismatch(
      'LICENSE_NUMBER',
      originalLicense.licenseNumber,
      form.values.license?.licenseNumber
    )
  const expirationConflict =
    aiValues?.expirationDate !== undefined &&
    // != null (not `!== undefined`): a stored null would pass the undefined
    // check and `new Date(null)` reads as 1970 — a guaranteed false conflict.
    originalLicense?.expirationDate != null &&
    valuesMismatch(
      'DATE',
      // GraphQL returns the stored date as an ISO string, which
      // formatDateString passes through unchanged — coerce to a Date first so
      // both sides compare as the same MM/DD/YYYY shape (otherwise the same
      // calendar day would flag a false "Double-check this").
      formatDateString(new Date(originalLicense.expirationDate)),
      formatDateString(form.values.license?.expirationDate)
    )

  // (b) borderline-confidence read still sitting in the field untouched
  const isBorderline = (field: keyof CdlPrefill) =>
    aiConfidence?.[field] !== undefined &&
    aiConfidence[field] < HIGH_CONFIDENCE_THRESHOLD
  const stateBorderline =
    aiValues?.state !== undefined &&
    isBorderline('state') &&
    !valuesMismatch('LICENSE_STATE', aiValues.state, form.values.license?.state)
  const classBorderline =
    aiValues?.licenseClass !== undefined &&
    isBorderline('licenseClass') &&
    !valuesMismatch(
      'LICENSE_CLASS',
      aiValues.licenseClass,
      form.values.license?.licenseClass
    )
  const licenseNumberBorderline =
    aiValues?.licenseNumber !== undefined &&
    isBorderline('licenseNumber') &&
    !valuesMismatch(
      'LICENSE_NUMBER',
      aiValues.licenseNumber,
      form.values.license?.licenseNumber
    )
  const expirationBorderline =
    aiValues?.expirationDate !== undefined &&
    isBorderline('expirationDate') &&
    !valuesMismatch(
      'DATE',
      formatDateString(aiValues.expirationDate),
      formatDateString(form.values.license?.expirationDate)
    )

  // Endorsement compares go through the same field-aware normalization the
  // recruiter mismatch checks use (order-insensitive, X ≡ H+N), joined to the
  // comma string that normalization expects. Array.isArray (not
  // `!== undefined`): GraphQL can return the stored list as null, which would
  // pass an undefined check and crash on .join.
  const endorsementsConflict =
    aiValues?.endorsements !== undefined &&
    Array.isArray(originalLicense?.endorsements) &&
    valuesMismatch(
      'LICENSE_ENDORSEMENTS',
      originalLicense.endorsements.join(','),
      (form.values.license?.endorsements ?? []).join(',')
    )
  const endorsementsBorderline =
    aiValues?.endorsements !== undefined &&
    isBorderline('endorsements') &&
    !valuesMismatch(
      'LICENSE_ENDORSEMENTS',
      aiValues.endorsements.join(','),
      (form.values.license?.endorsements ?? []).join(',')
    )

  const stateDoubleCheck = stateConflict || stateBorderline
  const classDoubleCheck = classConflict || classBorderline
  const licenseNumberDoubleCheck =
    licenseNumberConflict || licenseNumberBorderline
  const expirationDoubleCheck = expirationConflict || expirationBorderline
  const endorsementsDoubleCheck = endorsementsConflict || endorsementsBorderline

  const handlePreviousLicenseClassTypeChange = (value, index) => {
    form.setFieldValue(`previousLicenses.${index}.licenseClass`, value)
    if (value === LicenseClass.NO_CDL) {
      form.setFieldValue(`previousLicenses.${index}.endorsements`, [])
    } else {
      // Verify endorsements are valid for the new license class
      if (form.values.previousLicenses[index].endorsements.length > 0) {
        verifyPreviousLicenseEndorsements(index)
      }
    }

    if (
      isMedicalExamExpirationRequired(
        value,
        form.values.previousLicenses[index]?.country
      )
    ) {
      form.setFieldValue(
        `previousLicenses.${index}.medicalExamExpirationDate`,
        undefined
      )
    } else {
      form.setFieldValue(
        `previousLicenses.${index}.medicalExamExpirationDate`,
        null
      )
    }
  }

  useEffect(() => {
    // Ensure handleClassTypeChange is only called when there's a valid initial class type
    const initialClassType = form.values.license.licenseClass
    if (initialClassType) {
      handleClassTypeChange(initialClassType)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const verifyEndorsements = () => {
    const availableEndorsements = getEndorsementsByLicenseClass(
      form.values.license.licenseClass
    )

    const temp = form.values.license.endorsements
    const filteredEndorsements = temp.filter((endorsement) =>
      availableEndorsements.includes(endorsement)
    )
    form.setFieldValue('license.endorsements', filteredEndorsements)
  }

  const verifyPreviousLicenseEndorsements = (index) => {
    const licenseClass = form.values.previousLicenses[index].licenseClass
    const availableEndorsements = getEndorsementsByLicenseClass(licenseClass)
    const temp = form.values.previousLicenses[index].endorsements
    const filteredEndorsements = temp.filter((endorsement) =>
      availableEndorsements.includes(endorsement)
    )
    form.setFieldValue(
      `previousLicenses.${index}.endorsements`,
      filteredEndorsements
    )
  }

  useEffect(() => {
    if (form.values.license.licenseClass === LicenseClass.NO_CDL) return
    verifyEndorsements()
  }, [form.values.license.licenseClass])

  // Both scan UIs are gated on the company's Document Intelligence flag: with
  // DI off, the pipeline archives every driver upload unclassified, so the
  // scan would be a guaranteed dead end. `=== true` (not truthiness): the DB
  // value is never null, and a missing field (stale cache) fails safe by
  // hiding the scan UI.
  const canUseCdlScan =
    !!applicantId && application?.company?.documentIntelligenceEnabled === true
  // The banner mounts on mobile only. It also renders once autofill is running
  // or resolved (uploading / filled / rejected / error) so the in-place state
  // transitions from the mockup show above the fields; 'analyzing' and
  // 'unreadable' are full-screen takeovers returned above.
  const showCdlBanner = isMobile && canUseCdlScan
  // Desktop counterpart (ENG-4358): the persistent 250×250 rail left of the
  // fields. Every state lives inside the rail — no takeovers, no source sheet,
  // no camera on desktop.
  const showDesktopCdlRail = isDesktop && canUseCdlScan
  // While AI runs, the mockup replaces the whole form body with a centered
  // "Analyzing document…" + skip link (fields hidden). Uploading stays inline.
  const cdlAnalyzing = showCdlBanner && cdlStatus === 'analyzing'
  // Retake-first takeover (mockup Path 3) fires only for a confirmed CDL whose
  // read was too sparse (3+ AI-fillable fields below the confidence gate) — a
  // bad photo of the right document, where a retake genuinely helps. A non-CDL
  // upload ('rejected', e.g. a receipt) stays an inline banner: the fix there
  // is picking the right document, not photo quality tips.
  const cdlUnreadable = showCdlBanner && cdlStatus === 'unreadable'

  // The mockup's amber chip-warn, rendered BELOW the field (label → input →
  // chip, per the design), not inline in the label. "Please add" (required +
  // empty) takes precedence over "Double-check this" (conflict / borderline
  // read). Returns null when neither applies so the field stacks normally.
  const fieldNudge = ({
    needsAdd = false,
    doubleCheck = false,
  }: {
    needsAdd?: boolean
    doubleCheck?: boolean
  }) => {
    if (!needsAdd && !doubleCheck) return null
    return (
      <span className="mt-1 inline-flex w-fit items-center gap-1 rounded-full border border-doubleNickel-warning-200 bg-doubleNickel-warning-50 px-2 py-0.5 text-[10.5px] font-semibold text-doubleNickel-warning-700">
        <AlertTriangle size={11} />
        {needsAdd ? 'Please add' : 'Double-check this'}
      </span>
    )
  }

  // The six current-license fields, shared verbatim by the two layouts: the
  // classic full-width grid (mobile, and desktop without the scan rail) and
  // the desktop split layout right of the rail. Each field wraps in a plain
  // `flex flex-col` with no per-field bottom margin, so the container grid
  // owns all spacing in both layouts.
  const currentLicenseFields = (
    <>
      <div className="flex flex-col">
        {/* key: PlacesAutocomplete copies `value` into its own text state
            once at mount and never re-syncs, so an AI prefill would update
            the form invisibly (box keeps its stale mount-time text).
            Remounting when the AI state lands makes the initializer re-read
            the form value. */}
        <ApplicationAddressInput
          key={aiValues?.state ?? 'manual'}
          required
          applicationInput
          label="License State"
          placeholder="Select a state from the dropdown"
          value={form.values.license}
          types={['administrative_area_level_1']}
          error={form.errors[`license.state`]}
          handleChange={(value) => {
            form.setFieldValue(`license.state`, value.state)
            form.setFieldValue(`license.country`, value.country)
            if (value.country !== form.values.license.country) {
              form.setFieldValue(`license.licenseClass`, null)
              form.setFieldValue(`license.endorsements`, [])
            }
          }}
        />
        {fieldNudge({
          needsAdd: stateNeedsAdd,
          doubleCheck: stateDoubleCheck,
        })}
      </div>
      <div className="flex flex-col">
        <ApplicationSelectInput
          label="License Class"
          key={form.values.license?.country}
          required
          data={classTypes}
          value={form.values.license.licenseClass}
          error={form.errors[`license.licenseClass`] ?? ''}
          onChange={(event) => {
            handleClassTypeChange(event.currentTarget.value)
          }}
        />
        {fieldNudge({
          needsAdd: classNeedsAdd,
          doubleCheck: classDoubleCheck,
        })}
      </div>
      <div className="flex flex-col">
        <ApplicationTextInput
          label="License Number"
          required
          placeholder={'License Number'}
          {...form.getInputProps('license.licenseNumber')}
        />
        {fieldNudge({
          needsAdd: licenseNumberNeedsAdd,
          doubleCheck: licenseNumberDoubleCheck,
        })}
      </div>

      {form.values.license?.country === 'US' && (
        <div className="flex flex-col">
          <ApplicationMultiSelectInput
            searchable
            label="License Endorsements"
            disabled={endorsementDisabled}
            clearable
            data={getEndorsementsByLicenseClass(
              form.values.license.licenseClass
            ).map((endorsement) => ({
              value: endorsement,
              label: `${endorsement} - ${LicenseEndorsementDescriptions[endorsement]}`,
            }))}
            {...form.getInputProps('license.endorsements')}
            error={form.errors[`license.endorsements`] ?? ''}
          />
          {/* Endorsements are optional, so no "Please add" — only the
              conflict / borderline double-check applies. */}
          {fieldNudge({ doubleCheck: endorsementsDoubleCheck })}
        </div>
      )}

      <div className="flex flex-col">
        <ApplicationTextInput
          label="License Expiration Date"
          required
          pattern="\d*"
          placeholder={'MM / DD / YYYY'}
          value={formatDateString(form.values.license.expirationDate)}
          onChange={(event) => {
            formatDate(`license.expirationDate`, event, form)
          }}
          error={form.errors[`license.expirationDate`] ?? ''}
        />
        {fieldNudge({
          needsAdd: expirationNeedsAdd,
          doubleCheck: expirationDoubleCheck,
        })}
      </div>

      <div className="flex flex-col">
        <ApplicationTextInput
          label="Medical Exam Expiration Date"
          pattern="\d*"
          placeholder={'MM / DD / YYYY'}
          disabled={medicalExamDisabled}
          required={!medicalExamDisabled}
          value={formatDateString(
            form.values.license.medicalExamExpirationDate
          )}
          onChange={(event) => {
            formatDate(`license.medicalExamExpirationDate`, event, form)
          }}
          error={form.errors[`license.medicalExamExpirationDate`] ?? ''}
        />
        {fieldNudge({ needsAdd: medicalExamNeedsAdd })}
      </div>
    </>
  )

  // The license questions (suspended/revoked, previous licenses), shared by
  // the two layouts: inside the classic grid on mobile / no-rail desktop, and
  // full-width under the split on desktop with the rail — unchanged either way.
  // Answer handlers + expansion fragments, shared verbatim by the classic
  // grid layout and the desktop rail's row-per-question layout.
  const handleSuspendedChange = (e: string) => {
    form.setFieldValue('license.isLicenseSuspended', e === 'true')

    if (e !== 'true') {
      form.setFieldValue('license.licenseSuspendedReason', '')
    }
  }

  const handlePreviousLicensesChange = (e: string) => {
    if (e === 'false') {
      setHasPreviousLicenses(false)
      form.setFieldValue('previousLicenses', [])
    } else {
      setHasPreviousLicenses(true)
      if (
        !form.values.previousLicenses ||
        form.values.previousLicenses.length === 0
      ) {
        form.setFieldValue('previousLicenses', [
          {
            id: uuid(),
            licenseNumber: '',
            licenseClass: undefined,
            state: '',
            country: 'US',
            expirationDate: '',
            medicalExamExpirationDate: '',
            endorsements: [],
            isLicenseSuspended: undefined,
            licenseSuspendedReason: '',
          },
        ])
        setAccordionValue(uuid())
      }
    }
  }

  const handleTwicChange = (e: string) => {
    if (e === 'false') {
      setHasTWICCard(false)
      form.setFieldValue('twicCard.cardNumber', null)
      form.setFieldValue('twicCard.expirationDate', null)
    } else {
      setHasTWICCard(true)
    }
  }

  const suspensionReasonInput = (
    <ApplicationTextInput
      label="Please provide a reason for the suspension:"
      value={form.values.license?.licenseSuspendedReason}
      required
      error={form.errors[`license.licenseSuspendedReason`] ?? ''}
      onChange={(event) =>
        form.setFieldValue('license.licenseSuspendedReason', event.target.value)
      }
    />
  )

  const twicFieldInputs = (
    <>
      <ApplicationTextInput
        label="TWIC Number"
        placeholder={'TWIC number'}
        {...form.getInputProps('twicCard.cardNumber')}
      />
      <ApplicationTextInput
        label="TWIC Card Expiration Date"
        pattern="\d*"
        placeholder={'MM / DD / YYYY'}
        value={formatDateString(form.values.twicCard.expirationDate)}
        onChange={(event) => {
          formatDate(`twicCard.expirationDate`, event, form)
        }}
        error={form.errors[`twicCard.expirationDate`] ?? ''}
      />
    </>
  )

  const licenseQuestions = (
    <>
      <ApplicationRadioQuestion
        required
        className="sm:col-span-3"
        question="Have you had any license, permit, or privilege ever been suspended, revoked, or denied?"
        value={form.values.license?.isLicenseSuspended ? 'true' : 'false'}
        handleChange={handleSuspendedChange}
      />
      {form.values.license?.isLicenseSuspended && suspensionReasonInput}

      <ApplicationRadioQuestion
        required
        className="sm:col-span-3"
        question="Have you held any other licenses in the past 3 years?"
        value={hasPreviousLicenses ? 'true' : 'false'}
        handleChange={handlePreviousLicensesChange}
      />
    </>
  )

  // Previous-licenses accordion + "Add another" button, shared by the two
  // layouts: direct children of the screen wrapper's grid on mobile / no-rail
  // desktop (original placement), or part of the questions grid below the
  // split in the desktop rail layout — so the question blocks stack tightly
  // instead of inheriting the wrapper grid's 40px gaps.
  const previousLicensesSection = (
    <>
      {hasPreviousLicenses &&
        form?.values?.previousLicenses &&
        form.values.previousLicenses.length > 0 && (
          <Accordion
            chevronPosition="left"
            variant="separated"
            value={accordionValue}
            onChange={setAccordionValue}
            radius={'md'}
            classNames={{
              root: 'w-full col-span-3 mb-4',
              control: 'h-16',
              item: 'bg-transparent border-doubleNickel-gray-300 shadow-sm',
            }}
          >
            {form.values.previousLicenses.map((license, index) => {
              const endorsementDisabled =
                license.licenseClass === LicenseClass.NO_CDL
              const medicalExamDisabled = !isMedicalExamExpirationRequired(
                license.licenseClass,
                license.country
              )

              return (
                <ApplicationAccordionItem
                  value={license.id}
                  key={license.id}
                  title={
                    license.licenseNumber !== ''
                      ? `License: ${license.licenseNumber} (${license.state})`
                      : `Previous License ${index + 1}`
                  }
                  hasErrors={
                    findErrors(form.errors, 'previousLicenses', index) > 0
                  }
                  isCurrent={false}
                  handleDelete={() =>
                    form.removeListItem('previousLicenses', index)
                  }
                >
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
                    <ApplicationAddressInput
                      required
                      applicationInput
                      label="License State"
                      placeholder="Select a state from the dropdown"
                      value={license}
                      types={['administrative_area_level_1']}
                      error={form.errors[`previousLicenses.${index}.state`]}
                      handleChange={(value) => {
                        form.setFieldValue(
                          `previousLicenses.${index}.state`,
                          value.state
                        )
                        form.setFieldValue(
                          `previousLicenses.${index}.country`,
                          value.country
                        )
                        if (value.country !== license.country) {
                          form.setFieldValue(
                            `previousLicenses.${index}.licenseClass`,
                            null
                          )
                          form.setFieldValue(
                            `previousLicenses.${index}.endorsements`,
                            []
                          )
                        }
                      }}
                    />
                    <ApplicationSelectInput
                      label="License Class"
                      key={license?.country}
                      required
                      data={previousClassTypes(license?.country)}
                      value={license.licenseClass}
                      error={
                        form.errors[`previousLicenses.${index}.licenseClass`] ??
                        ''
                      }
                      onChange={(event) => {
                        handlePreviousLicenseClassTypeChange(
                          event.currentTarget.value,
                          index
                        )
                      }}
                    />
                    <ApplicationTextInput
                      label="License Number"
                      required
                      placeholder={'License Number'}
                      {...form.getInputProps(
                        `previousLicenses.${index}.licenseNumber`
                      )}
                    />

                    {license?.country === 'US' && (
                      <ApplicationMultiSelectInput
                        className="col-span-full"
                        searchable
                        label="License Endorsements"
                        disabled={endorsementDisabled}
                        clearable
                        data={getEndorsementsByLicenseClass(
                          license.licenseClass
                        ).map((endorsement) => ({
                          value: endorsement,
                          label: `${endorsement} - ${LicenseEndorsementDescriptions[endorsement]}`,
                        }))}
                        {...form.getInputProps(
                          `previousLicenses.${index}.endorsements`
                        )}
                        error={
                          form.errors[
                            `previousLicenses.${index}.endorsements`
                          ] ?? ''
                        }
                      />
                    )}

                    <ApplicationTextInput
                      label="License Expiration Date"
                      pattern="\d*"
                      required
                      placeholder={'MM / DD / YYYY'}
                      value={formatDateString(license.expirationDate)}
                      onChange={(event) => {
                        formatDate(
                          `previousLicenses.${index}.expirationDate`,
                          event,
                          form
                        )
                      }}
                      error={
                        form.errors[
                          `previousLicenses.${index}.expirationDate`
                        ] ?? ''
                      }
                    />

                    <ApplicationTextInput
                      label="Medical Exam Expiration Date"
                      pattern="\d*"
                      placeholder={'MM / DD / YYYY'}
                      disabled={medicalExamDisabled}
                      required={!medicalExamDisabled}
                      value={formatDateString(
                        license.medicalExamExpirationDate
                      )}
                      onChange={(event) => {
                        formatDate(
                          `previousLicenses.${index}.medicalExamExpirationDate`,
                          event,
                          form
                        )
                      }}
                      error={
                        form.errors[
                          `previousLicenses.${index}.medicalExamExpirationDate`
                        ] ?? ''
                      }
                    />

                    <ApplicationRadioQuestion
                      required
                      question="Was this license, permit, or privilege ever suspended, revoked, or denied?"
                      value={license?.isLicenseSuspended ? 'true' : 'false'}
                      handleChange={(e) => {
                        form.setFieldValue(
                          `previousLicenses.${index}.isLicenseSuspended`,
                          e === 'true'
                        )

                        if (e !== 'true') {
                          form.setFieldValue(
                            `previousLicenses.${index}.licenseSuspendedReason`,
                            ''
                          )
                        }
                      }}
                    />
                    {license?.isLicenseSuspended && (
                      <ApplicationTextInput
                        label="Please provide a reason for the suspension:"
                        value={license?.licenseSuspendedReason}
                        required
                        error={
                          form.errors[
                            `previousLicenses.${index}.licenseSuspendedReason`
                          ] ?? ''
                        }
                        onChange={(event) =>
                          form.setFieldValue(
                            `previousLicenses.${index}.licenseSuspendedReason`,
                            event.target.value
                          )
                        }
                      />
                    )}
                  </div>
                </ApplicationAccordionItem>
              )
            })}
          </Accordion>
        )}

      {hasPreviousLicenses && (
        <MantineButton
          size="sm"
          className="col-span-3 mb-4"
          variant="light"
          classNames={{
            root: 'h-11 rounded-lg border border-doubleNickel-brand-500 bg-doubleNickel-brand-25 text-sm font-semibold text-doubleNickel-brand-700 hover:bg-doubleNickel-brand-50',
          }}
          onClick={() => {
            setAccordionValue(`${uuid()}`)
            form.setFieldValue('previousLicenses', [
              ...(form.values.previousLicenses || []),
              {
                id: uuid(),
                licenseNumber: '',
                licenseClass: undefined,
                state: '',
                country: 'US',
                expirationDate: '',
                medicalExamExpirationDate: '',
                endorsements: [],
                isLicenseSuspended: undefined,
                licenseSuspendedReason: '',
              },
            ])
          }}
        >
          Add another previous license
        </MantineButton>
      )}
    </>
  )

  // TWIC card question + conditional fields, shared the same way.
  const twicSection = (
    <>
      <ApplicationRadioQuestion
        className="sm:col-span-3"
        question="Do you have a TWIC card?"
        required
        value={hasTWICCard ? 'true' : 'false'}
        handleChange={handleTwicChange}
      />

      {hasTWICCard && twicFieldInputs}
    </>
  )

  // Analyzing takeover: full-body centered state + "Skip" link, footer hidden —
  // matches the mockup's "4 · Analyzing…" screen.
  if (cdlAnalyzing) {
    return (
      <ApplicationScreenWrapper
        title={'License Information'}
        description={
          'Please provide information about your current license and any previous licenses you have held.'
        }
        goBack={goBack}
        application={application}
        hideButtons
      >
        {leaveGuardModal}
        <div className="col-span-3 flex min-h-[50vh] w-full flex-col items-center justify-center gap-4 text-center">
          <style>{`@keyframes cdl-sparkle-spin{to{transform:rotate(360deg)}}@keyframes cdl-sparkle-glow{0%,100%{filter:drop-shadow(0 0 3px #1C93D760)}50%{filter:drop-shadow(0 0 10px #1C93D7AA)}}`}</style>
          <div className="flex flex-col items-center gap-3 rounded-2xl bg-doubleNickel-brand-25 px-8 py-7">
            <span
              style={{
                color: '#1C93D7',
                display: 'inline-flex',
                animation:
                  'cdl-sparkle-spin 2.5s linear infinite, cdl-sparkle-glow 2s ease-in-out infinite',
              }}
            >
              <IconSparkle size={30} />
            </span>
            <span className="text-sm font-semibold text-doubleNickel-brand-700">
              {hadRejectedScan
                ? 'Re-reading your CDL front…'
                : 'Analyzing document…'}
            </span>
          </div>
          <button
            type="button"
            onClick={handleCdlReset}
            className="text-sm font-semibold text-doubleNickel-brand-600 underline"
          >
            Skip — I&apos;ll type it in myself
          </button>
        </div>
      </ApplicationScreenWrapper>
    )
  }

  // Couldn't-read takeover (mockup Path 3 · screen 2): full-body retake-first
  // recovery — no form title/fields, and no footer buttons (like the analyzing
  // takeover): the screen's own actions are the only way forward. "Retake
  // photo" reopens the camera, "Upload a different photo" the picker; both
  // restart the scan (superseding the unreadable document). Manual entry exits
  // to the untouched form.
  if (cdlUnreadable) {
    return (
      <ApplicationScreenWrapper
        goBack={goBack}
        application={application}
        hideButtons
      >
        {leaveGuardModal}
        <CdlRetakeContent
          returningApplicant={returningApplicant}
          onFileSelected={handleCdlFileSelected}
          onManualEntry={handleCdlReset}
        />
      </ApplicationScreenWrapper>
    )
  }

  return (
    <ApplicationScreenWrapper
      title={'License Information'}
      description={
        'Please provide information about your current license and any previous licenses you have held.'
      }
      goBack={goBack}
      updateLoading={updateLoading}
      application={application}
      handleCancel={goBack}
      backText={backText}
      saveButtontext={'Save and continue'}
      // Mobile can't submit mid-scan (the analyzing takeover hides the
      // footer); the desktop split keeps the footer visible, so enforce the
      // same invariant by disabling Continue while a scan is in flight —
      // otherwise the step could be submitted before autofill resolves.
      saveButtonDisabled={
        showDesktopCdlRail &&
        (cdlStatus === 'uploading' || cdlStatus === 'analyzing')
      }
      handleSubmit={() => {
        // How the license fields got filled (ENG-4304): a submit without AI
        // values IS the manual route — whether the driver skipped the scan or
        // never opened it. `scan_status` separates never-tried ('idle') from
        // bailed/failed ('rejected'/'error').
        datadogRum.addAction('driver_license_step_submitted', {
          component: 'LicenseScreen',
          ai_prefill_used: !!aiValues,
          scan_status: cdlStatus,
          is_mobile: isMobile,
          // Named platform split (ENG-4362): desktop submits can be
          // AI-assisted since #3698, so queries should read the concept
          // instead of inferring it from a boolean. is_mobile stays for
          // continuity with existing queries.
          platform: isMobile ? 'mobile' : 'desktop',
          returning_applicant: returningApplicant,
        })
        setAccordionValue(null)
        handleNext(buildLicensePayload(form.values))
      }}
    >
      {leaveGuardModal}
      {/* Current License Section */}
      <div className="col-span-3 mb-8">
        {/* Mobile-only CDL scan/upload autofill banner. */}
        {showCdlBanner && (
          <CdlScanBanner
            returningApplicant={returningApplicant}
            status={cdlStatus}
            onFileSelected={handleCdlFileSelected}
            onReset={handleCdlReset}
          />
        )}
        {showDesktopCdlRail ? (
          <>
            {/* Green done-banner above the split (mockup Path 1 · 4). Rescan
                resets the rail back to the dropzone — it does not open the
                picker directly. */}
            {cdlStatus === 'filled' && (
              <div className="mb-[18px] flex items-center gap-2.5 rounded-lg border border-doubleNickel-success-200 bg-doubleNickel-success-50 px-3 py-2.5">
                <span className="flex-none text-doubleNickel-success-700">
                  <Check size={18} strokeWidth={2.2} />
                </span>
                <span className="flex-1 text-[12.5px] font-semibold text-doubleNickel-success-700">
                  Filled from your CDL front
                </span>
                <button
                  type="button"
                  onClick={handleCdlReset}
                  className="flex-none text-xs text-doubleNickel-brand-600 underline"
                >
                  Rescan
                </button>
              </div>
            )}
            {/* Split layout (mockup `.layout`): document rail left at one
                third of the width, fields right — so the photo column matches
                each of the two field columns. */}
            <div className="mb-4 grid grid-cols-[1fr_2fr] items-start gap-[26px]">
              <CdlScanDesktopRail
                returningApplicant={returningApplicant}
                status={cdlStatus}
                reReading={hadRejectedScan}
                fileName={cdlScanFile?.name ?? null}
                previewUrl={cdlPreviewUrl}
                onFileSelected={handleDesktopFileSelected}
                onSkip={handleCdlReset}
              />
              {/* Fields (mockup `.grid2`): 1 column on tablet (640–768px),
                  2 columns ≥ md. Dimmed and truly inert while the AI reads —
                  `inert` blocks keyboard focus/typing too, so a driver can't
                  edit a field mid-scan only to have the prefill overwrite it
                  (pointer-events alone stops just the mouse). The Skip link
                  lives inside the rail, outside this subtree. Empty string is
                  the React 18 attribute passthrough; typed support is React 19. */}
              <div
                // @ts-expect-error — `inert` is missing from React 18 typings
                inert={cdlStatus === 'analyzing' ? '' : undefined}
                className={`grid grid-cols-1 gap-x-[22px] gap-y-[18px] md:grid-cols-2 ${
                  cdlStatus === 'analyzing'
                    ? 'pointer-events-none opacity-[.45]'
                    : ''
                }`}
              >
                {currentLicenseFields}
              </div>
            </div>
            {/* Separator between the split and the full-width questions
                (mockup `.qsep`). */}
            <div className="mb-4 mt-1.5 h-px bg-doubleNickel-gray-200" />
            {/* Row-per-question (approved Idea 1, cdl-questions-ideas.html):
                question label left, Yes/No pinned right, and each answer's
                expansion attaches under its OWN row — so no answer can ever
                move another question (the previous auto-placed grid kept
                re-homing TWIC whenever an expansion injected cells). Hairline
                dividers group the rows; the previous-licenses accordion is
                simply a full-width child of its row, no special casing. */}
            <div className="flex flex-col">
              <div className="border-b border-doubleNickel-gray-100 py-3.5">
                <ApplicationRadioQuestion
                  required
                  inline
                  question="Have you had any license, permit, or privilege ever been suspended, revoked, or denied?"
                  value={
                    form.values.license?.isLicenseSuspended ? 'true' : 'false'
                  }
                  handleChange={handleSuspendedChange}
                />
                {form.values.license?.isLicenseSuspended && (
                  <div className="mt-3 max-w-[520px]">
                    {suspensionReasonInput}
                  </div>
                )}
              </div>
              <div className="border-b border-doubleNickel-gray-100 py-3.5">
                <ApplicationRadioQuestion
                  required
                  inline
                  question="Have you held any other licenses in the past 3 years?"
                  value={hasPreviousLicenses ? 'true' : 'false'}
                  handleChange={handlePreviousLicensesChange}
                />
                {hasPreviousLicenses && (
                  <div className="mt-3">{previousLicensesSection}</div>
                )}
              </div>
              <div className="py-3.5">
                <ApplicationRadioQuestion
                  required
                  inline
                  question="Do you have a TWIC card?"
                  value={hasTWICCard ? 'true' : 'false'}
                  handleChange={handleTwicChange}
                />
                {hasTWICCard && (
                  <div className="mt-3 grid max-w-[560px] grid-cols-1 gap-4 md:grid-cols-2">
                    {twicFieldInputs}
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
            {currentLicenseFields}
            <div className="sm:col-span-3" />
            {licenseQuestions}
          </div>
        )}
      </div>

      {/* Previous licenses + TWIC: original wrapper-grid placement for the
          classic layout; the desktop rail layout renders them inside the
          questions grid above instead, so the questions stack tightly. */}
      {!showDesktopCdlRail && (
        <>
          {previousLicensesSection}
          {twicSection}
        </>
      )}
    </ApplicationScreenWrapper>
  )
}
